import { Button } from "@/components/ui/Button";
import { Stack } from "@/components/ui/Stack";
import { Text } from "@/components/ui/Text";
import { useTelemetry } from "@/contexts/Telemetry";
import { safeExternalHttpUrl } from "@/lib/safe-external-url";
// Generated by the SDK for `usage.createStripePortalSession`, which lands
// separately. If generation names the hook differently, only this import and
// the `portal.mutate` call below have to follow it.
import { useCreateStripePortalSessionMutation } from "@gram/client/react-query/createStripePortalSession.js";
import { useCallback, useState } from "react";

const DEFAULT_LABEL = "Manage billing";
const ERROR_MESSAGE = "Couldn't open the billing portal. Try again.";

/**
 * Hands an admin the Stripe customer portal, where cards, billing details, and
 * invoice PDFs live.
 *
 * The portal session is minted per click rather than at render: the link is
 * single-use and short-lived, so one created on page load would already have
 * expired by the time anyone pressed it.
 *
 * `label` narrows the offer to the one thing a caller is asking for — the
 * payment-failure banner sends an admin to update a card, not to browse
 * invoices — while the portal handoff, its URL check, and the audited mutation
 * behind it stay in one place.
 */
export function PaygPortalButton({
  label = DEFAULT_LABEL,
}: {
  label?: string;
} = {}): JSX.Element {
  const telemetry = useTelemetry();
  const portal = useCreateStripePortalSessionMutation();
  const [failed, setFailed] = useState(false);

  const openPortal = useCallback(() => {
    setFailed(false);

    portal.mutate(
      {},
      {
        onSuccess: (link) => {
          // The link crosses a trust boundary, so it is validated at the
          // navigation sink rather than trusted because it came from our API.
          const url = safeExternalHttpUrl(link);
          if (url === null) {
            telemetry.capture("stripe_portal_error", {
              error: "unusable portal link",
            });
            setFailed(true);
            return;
          }

          // Stripe returns the customer to the dashboard when they leave the
          // portal, so this is a handoff rather than a new tab.
          window.location.assign(url);
        },
        onError: (error: unknown) => {
          telemetry.capture("stripe_portal_error", {
            error: error instanceof Error ? error.message : "unknown",
          });
          setFailed(true);
        },
      },
    );
  }, [portal, telemetry]);

  return (
    <Stack gap={2} align="start">
      <Button
        variant="primary"
        onClick={openPortal}
        disabled={portal.isPending}
        icon="arrow-up-right"
        iconAfter={true}
      >
        {portal.isPending ? "OPENING..." : label}
      </Button>
      {failed && (
        <Text small destructive role="alert">
          {ERROR_MESSAGE}
        </Text>
      )}
    </Stack>
  );
}
